<template>
  <v-container color="primary" class="pa-4" style="max-width: 1400px;">

    <!-- Statistics Widgets -->
    <v-layout row wrap class="mb-4" v-if="stats.total > 0 || stats.total === 0">
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center blue lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold blue--text text--darken-4">{{ stats.total }}</div>
            <div class="caption blue--text text--darken-2 font-weight-bold">Total Espacios</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center green lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold green--text text--darken-4">{{ stats.available }}</div>
            <div class="caption green--text text--darken-2 font-weight-bold">Disponibles</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center red lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold red--text text--darken-4">{{ stats.occupied }}</div>
            <div class="caption red--text text--darken-2 font-weight-bold">Ocupados</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center orange lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold orange--text text--darken-4">{{ stats.reserved }}</div>
            <div class="caption orange--text text--darken-2 font-weight-bold">Reservados</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center grey lighten-3">
          <v-card-text>
            <div class="display-1 font-weight-bold grey--text text--darken-3">{{ stats.inactive }}</div>
            <div class="caption grey--text text--darken-2 font-weight-bold">Inactivos</div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Main Card -->
    <v-card class="rounded-lg elevation-4 overflow-hidden">
      <v-toolbar flat color="white" class="border-bottom" style="height: auto; min-height: 56px;">
        <v-icon color="primary" class="mr-2">local_parking</v-icon>
        <v-toolbar-title class="font-weight-bold primary--text" style="font-size: 1.5rem; white-space: normal; line-height: 1.3;">
          Mapa de Estacionamiento (Vista Informativa)
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- Refresh interval -->
        <div style="width: 120px; margin-right: 6px;" class="d-flex align-center hidden-xs-only">
          <v-text-field
            v-model.number="refreshMinutes"
            label="Min recarga"
            type="number"
            min="1"
            dense
            outlined
            hide-details
            @change="updateRefreshInterval"
            color="primary"
          ></v-text-field>
        </div>
        <v-btn icon color="primary" @click="fetchAll" :loading="loading" title="Recargar">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn icon color="primary" @click="toggleFullscreen">
          <v-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-4 pa-sm-6">

        <!-- Date navigation + filters -->
        <div class="d-flex align-center justify-space-between mb-5 flex-wrap" style="gap: 8px;">
          <!-- Date navigation -->
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <v-btn small depressed color="primary lighten-4" @click="goPrev" :loading="loading">
              <v-icon color="primary">chevron_left</v-icon>
            </v-btn>
            <v-menu
              ref="menuFecha"
              v-model="menuFecha"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
              :z-index="2000"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="date-display px-4 py-2 rounded-pill primary--text font-weight-bold pointer d-flex align-center"
                  v-bind="attrs" v-on="on"
                  style="background: rgba(25,118,210,0.08); cursor: pointer; font-size: 0.85rem;"
                >
                  <v-icon color="primary" class="mr-2" small>calendar_today</v-icon>
                  {{ currentDate }}
                </div>
              </template>
              <v-date-picker
                v-model="currentDate"
                :locale="$t('region')"
                @change="onDateChange"
                @input="menuFecha = false"
                color="primary"
              />
            </v-menu>
            <v-btn small depressed color="primary lighten-4" @click="goNext" :loading="loading">
              <v-icon color="primary">chevron_right</v-icon>
            </v-btn>
            <v-btn small depressed color="primary" @click="goToday">
              <v-icon small class="mr-1">today</v-icon>Hoy
            </v-btn>
          </div>

          <!-- Section filter -->
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <v-btn-toggle v-model="selectedSectionFilter" mandatory dense>
              <v-btn small value="all" color="white">
                <v-icon small class="mr-1">apps</v-icon>Todas
              </v-btn>
              <v-btn v-for="sec in parkingSections" :key="sec.id" small :value="sec.id" color="white">
                {{ sec.name }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <!-- Legend (centrada y responsiva) -->
        <div class="d-flex justify-center flex-wrap mb-5" style="gap: 16px;">
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#4CAF50;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Disponible</span>
          </div>
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#F44336;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Ocupado</span>
          </div>
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#FF9800;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Reservado</span>
          </div>
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#9E9E9E;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Inactivo</span>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="d-flex justify-center align-center pa-10">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </div>

        <!-- Parking grid by section -->
        <div v-else-if="filteredSections.length === 0" class="text-center pa-10 grey--text">
          <v-icon large color="grey lighten-1">local_parking</v-icon>
          <div class="subheading mt-2">No hay secciones de estacionamiento configuradas.</div>
        </div>

        <div v-else>
          <div v-for="section in filteredSections" :key="section.id" class="mb-6">
            <!-- Section header -->
            <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
              <v-icon color="primary">business</v-icon>
              <span class="title font-weight-bold primary--text">{{ section.name }}</span>
              <v-chip small color="blue lighten-5" text-color="blue darken-3">
                {{ spacesBySection(section.id).length }} espacios
              </v-chip>
            </div>

            <!-- Spaces grid -->
            <v-layout row wrap>
              <v-flex
                v-for="space in spacesBySection(section.id)"
                :key="space.id"
                xs6 sm4 md3 lg2
              >
                <v-card
                  class="ma-1 parking-space-card elevation-2"
                  :class="getSpaceCardClass(space)"
                  @click="openSpaceDetail(space)"
                  style="cursor: pointer; border-radius: 10px; transition: transform 0.15s; min-height: 100px;"
                >
                  <v-card-text class="pa-3 d-flex flex-column align-center justify-center text-center" style="height:100%;">
                    <v-icon :color="getSpaceIconColor(space)" large>
                      {{ getSpaceIcon(space) }}
                    </v-icon>
                    <div class="font-weight-bold mt-1" style="font-size: 0.95rem;">{{ space.number }}</div>
                    <div class="caption mt-1" style="opacity: 0.8;">{{ space.space_type_name || 'General' }}</div>
                    <v-chip x-small class="mt-2 font-weight-bold" :color="getSpaceChipColor(space)" dark>
                      {{ getSpaceStatusLabel(space) }}
                    </v-chip>
                    <!-- Guest name if occupied/reserved -->
                    <div v-if="getSpaceGuest(space)" class="caption mt-1 text-truncate" style="max-width:100%; font-weight:600; opacity:0.85;">
                      {{ getSpaceGuest(space) }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>

            <v-divider v-if="filteredSections.indexOf(section) < filteredSections.length -1" class="mt-4"></v-divider>
          </div>
        </div>

      </v-card-text>
    </v-card>


    <!-- Space detail dialog -->
    <v-dialog v-model="dialogSpaceDetail" max-width="480px" v-if="selectedSpace">
      <v-card class="rounded-xl elevation-8 overflow-hidden">
        <v-toolbar flat dark :color="getSpaceChipColor(selectedSpace)" class="premium-toolbar">
          <v-icon left>local_parking</v-icon>
          <v-toolbar-title class="font-weight-bold">Espacio {{ selectedSpace.number }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogSpaceDetail = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6 mt-2">
          <v-layout column>
            <!-- Status chip -->
            <v-flex class="mb-4 text-center">
              <v-chip
                :color="getSpaceChipColor(selectedSpace)"
                dark label
                class="px-4 font-weight-bold headline mb-2"
                style="height:auto; padding: 8px 16px; border-radius: 12px;"
              >
                {{ getSpaceStatusLabel(selectedSpace) }}
              </v-chip>
              <div class="grey--text text--darken-1 font-weight-medium">Estado del espacio</div>
            </v-flex>

            <v-divider class="mb-5"></v-divider>

            <v-layout row wrap>
              <!-- Space number -->
              <v-flex xs6 class="mb-4 pr-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">local_parking</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Espacio</div>
                    <div class="body-2 font-weight-bold">{{ selectedSpace.number }}</div>
                  </div>
                </div>
              </v-flex>

              <!-- Type -->
              <v-flex xs6 class="mb-4 pl-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">commute</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Tipo</div>
                    <div class="body-2 font-weight-bold">{{ selectedSpace.space_type_name || 'General' }}</div>
                  </div>
                </div>
              </v-flex>

              <!-- Section -->
              <v-flex xs6 class="mb-4 pr-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">business</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Sección</div>
                    <div class="body-2 font-weight-bold">{{ getSectionName(selectedSpace.id_section) }}</div>
                  </div>
                </div>
              </v-flex>

              <!-- Date -->
              <v-flex xs6 class="mb-4 pl-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">calendar_today</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Fecha</div>
                    <div class="body-2 font-weight-bold">{{ currentDate.split('-').reverse().join('/') }}</div>
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <!-- Occupied: show access record -->
            <div v-if="getSpaceAccessRecord(selectedSpace)">
              <v-divider class="mb-4"></v-divider>
              <div class="caption grey--text font-weight-bold text-uppercase mb-3">Información de Acceso</div>

              <v-layout row wrap>
                <v-flex xs12 class="mb-3">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">person</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Visitante</div>
                      <div class="body-2 font-weight-bold">{{ getInvitationDetail(selectedSpace).visitor_name }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs12 class="mb-3" v-if="getGuestCompany(selectedSpace)">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">business</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Empresa Visitante</div>
                      <div class="body-2 font-weight-bold">{{ getGuestCompany(selectedSpace) }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs12 class="mb-3" v-if="getInvitationDetail(selectedSpace) && getInvitationDetail(selectedSpace).host_company_name">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">business_center</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Empresa Anfitriona</div>
                      <div class="body-2 font-weight-bold">{{ getInvitationDetail(selectedSpace).host_company_name }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs6 class="mb-3 pr-2" v-if="getSpaceAccessRecord(selectedSpace)">
                  <div class="d-flex align-start">
                    <v-icon color="orange" class="mr-3 mt-1">login</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Hora Entrada</div>
                      <div class="body-2 font-weight-bold orange--text">{{ getInvitationDetail(selectedSpace).access_time || 'N/A' }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs6 class="mb-3 pl-2" v-if="getSpaceAccessRecord(selectedSpace)">
                  <div class="d-flex align-start">
                    <v-icon :color="getInvitationDetail(selectedSpace).exit_time ? 'success' : 'grey'" class="mr-3 mt-1">logout</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Hora Salida</div>
                      <div class="body-2 font-weight-bold" :class="getInvitationDetail(selectedSpace).exit_time ? 'success--text' : 'grey--text'">
                        {{ getInvitationDetail(selectedSpace).exit_time || 'Pendiente' }}
                      </div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs12 class="mb-2" v-if="getInvitationDetail(selectedSpace) && getInvitationDetail(selectedSpace).registered_by_name">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">security</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Registrado por</div>
                      <div class="body-2 font-weight-bold">{{ getInvitationDetail(selectedSpace).registered_by_name }}</div>
                    </div>
                  </div>
                </v-flex>
              </v-layout>
            </div>

            <!-- Reserved: show invitation data -->
            <div v-else-if="getSpaceReservation(selectedSpace)">
              <v-divider class="mb-4"></v-divider>
              <div class="caption grey--text font-weight-bold text-uppercase mb-3">Información de Reserva</div>

              <v-layout row wrap>
                <v-flex xs12 class="mb-3">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">person</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Visitante</div>
                      <div class="body-2 font-weight-bold">{{ getInvitationDetail(selectedSpace).visitor_name }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs12 class="mb-3" v-if="getGuestCompany(selectedSpace)">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">business</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Empresa Visitante</div>
                      <div class="body-2 font-weight-bold">{{ getGuestCompany(selectedSpace) }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs12 class="mb-3" v-if="getInvitationDetail(selectedSpace) && getInvitationDetail(selectedSpace).host_company_name">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">business_center</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Empresa Anfitriona</div>
                      <div class="body-2 font-weight-bold">{{ getInvitationDetail(selectedSpace).host_company_name }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs6 class="mb-3 pr-2">
                  <div class="d-flex align-start">
                    <v-icon color="orange" class="mr-3 mt-1">event</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Fecha Inicio</div>
                      <div class="body-2 font-weight-bold">{{ getInvitationDetail(selectedSpace).reservation_start_date }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs6 class="mb-3 pl-2">
                  <div class="d-flex align-start">
                    <v-icon color="orange" class="mr-3 mt-1">event_busy</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Fecha Fin</div>
                      <div class="body-2 font-weight-bold">{{ getInvitationDetail(selectedSpace).reservation_end_date }}</div>
                    </div>
                  </div>
                </v-flex>
                <v-flex xs12 v-if="getInvitationDetail(selectedSpace) && getInvitationDetail(selectedSpace).invitation_subject">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">notes</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Asunto</div>
                      <div class="body-2 font-weight-medium">{{ getInvitationDetail(selectedSpace).invitation_subject }}</div>
                    </div>
                  </div>
                </v-flex>
              </v-layout>
            </div>

            <!-- Available -->
            <v-alert
              v-if="getSpaceStatus(selectedSpace) === 'available'"
              type="success"
              outline
              color="green"
              class="rounded-lg mt-2"
              icon="check_circle"
            >
              Este espacio está disponible para la fecha seleccionada.
            </v-alert>

          </v-layout>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn
            depressed
            color="primary"
            dark
            class="text-none font-weight-bold"
            @click="openChangeStatusDialog"
            style="border-radius: 12px;"
          >
            <v-icon left small>edit</v-icon>
            Cambiar Estado
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn block depressed large color="grey lighten-2" class="text-none font-weight-bold" @click="dialogSpaceDetail = false" style="border-radius: 12px;">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change Status Dialog -->
    <v-dialog v-model="dialogChangeStatus" max-width="500px" v-if="selectedSpace">
      <v-card class="rounded-xl elevation-8">
        <v-toolbar flat dark color="primary" class="premium-toolbar">
          <v-icon left>edit</v-icon>
          <v-toolbar-title class="font-weight-bold">Cambiar Estado - Espacio {{ selectedSpace.number }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogChangeStatus = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6 mt-4">
          <v-form ref="formChangeStatus">
            <!-- Current Status -->
            <v-layout column>
              <v-flex xs12 class="mb-6">
                <div class="caption grey--text font-weight-bold text-uppercase mb-2">Estado Actual</div>
                <v-chip
                  :color="getSpaceChipColor(selectedSpace)"
                  dark
                  class="px-4 font-weight-bold"
                  style="height: auto; padding: 8px 16px; border-radius: 12px;"
                >
                  {{ getSpaceStatusLabel(selectedSpace) }}
                </v-chip>
              </v-flex>

              <v-divider class="mb-6"></v-divider>

              <!-- Status Select -->
              <v-flex xs12 class="mb-6">
                <v-select
                  v-model="changeStatusForm.status"
                  :items="statusOptions"
                  label="Nuevo Estado"
                  prepend-icon="local_parking"
                  outlined
                  dense
                  :rules="[v => !!v || 'Selecciona un estado']"
                ></v-select>
              </v-flex>

              <!-- Reason (Optional) -->
              <v-flex xs12 class="mb-4">
                <v-textarea
                  v-model="changeStatusForm.reason"
                  label="Razón del cambio (opcional)"
                  placeholder="Ej: liberación manual por error"
                  prepend-icon="description"
                  outlined
                  dense
                  rows="3"
                  counter="256"
                  maxlength="256"
                  hint="Máximo 256 caracteres"
                ></v-textarea>
              </v-flex>

              <!-- Error Alert -->
              <v-alert
                v-if="changeStatusError"
                type="error"
                outlined
                dense
                class="mb-4"
                dismissible
                @input="changeStatusError = null"
              >
                {{ changeStatusError }}
              </v-alert>

              <!-- Info Alert -->
              <v-alert
                type="info"
                outlined
                dense
                icon="info"
                class="mb-4"
              >
                <strong>Nota:</strong> Solo administradores de la empresa pueden cambiar el estado de los espacios.
              </v-alert>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn color="grey" @click="dialogChangeStatus = false">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            dark
            :loading="loadingChangeStatus"
            :disabled="!changeStatusForm.status || loadingChangeStatus"
            @click="handleChangeStatus"
          >
            <v-icon left small>check_circle</v-icon>
            Cambiar Estado
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import '@/plugins/axios'
import axios from 'axios'
import store from '@/store'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'mockup5',
  data: () => ({
    currentDate: new Date().toISOString().substr(0, 10),
    menuFecha: false,
    loading: false,
    isFullscreen: false,
    refreshMinutes: 2,
    refreshIntervalId: null,

    // Data from API
    id_empresa: '',
    parkingSections: [],
    parkingSpaces: [],
    invitationsStatus: [],

    // UI state
    selectedSectionFilter: 'all',
    dialogSpaceDetail: false,
    selectedSpace: null,

    // Change status UI
    dialogChangeStatus: false,
    loadingChangeStatus: false,
    changeStatusError: null,
    changeStatusForm: {
      status: 'available',
      reason: ''
    },
    statusOptions: [
      { text: 'Disponible', value: 'available' },
      { text: 'Ocupado', value: 'occupied' },
      { text: 'Reservado', value: 'reserved' }
    ],
  }),

  created() {
    this.get_extra_data()
  },

  mounted() {
    this.startAutoRefresh()
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
  },

  beforeDestroy() {
    if (this.refreshIntervalId) clearInterval(this.refreshIntervalId)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    if (this.isFullscreen && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {})
    }
    const appEl = document.getElementById('app')
    if (appEl) appEl.classList.remove('hide-layout-elements-fullscreen')
  },

  computed: {
    ...mapGetters(['getProfile']),
    ...mapState(['language']),

    filteredSections() {
      if (this.selectedSectionFilter === 'all') return this.parkingSections
      return this.parkingSections.filter(s => s.id === this.selectedSectionFilter)
    },

    stats() {
      const spaces = this.parkingSpaces
      let available = 0, occupied = 0, reserved = 0, inactive = 0
      spaces.forEach(sp => {
        const status = this.getSpaceStatus(sp)
        if (status === 'occupied') occupied++
        else if (status === 'reserved') reserved++
        else if (status === 'inactive') inactive++
        else available++
      })
      return {
        total: spaces.length,
        available,
        occupied,
        reserved,
        inactive,
      }
    }
  },

  methods: {
    normalizeApiDate(dateStr) {
      if (!dateStr || typeof dateStr !== 'string') return ''
      const cleanDate = dateStr.split('T')[0].split(' ')[0]
      const p = cleanDate.split('-')
      if (p.length !== 3) return cleanDate
      if (p[0].length === 4) return cleanDate
      return `${p[2]}-${p[1]}-${p[0]}`
    },
    isReservationOnSelectedDate(reservation) {
      const startDate = this.normalizeApiDate(reservation && reservation.invitation_date)
      const endDateRaw = reservation && reservation.expiration ? reservation.expiration : reservation && reservation.invitation_date
      const endDate = this.normalizeApiDate(endDateRaw)
      if (!startDate || !endDate) return false
      return startDate <= this.currentDate && this.currentDate <= endDate
    },
    get_extra_data() {
      const config = {
        method: 'get',
        url: 'https://empresa-service.intrare.app/auth/me',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.id_empresa = response.data.id_company
          this.fetchAll()
        })
        .catch(err => console.log(err))
    },

    async fetchAll() {
      this.loading = true
      try {
        await Promise.all([
          this.getParkingSections(),
          this.getParkingSpaces(),
          this.getInvitationsStatus(),
        ])
      } finally {
        this.loading = false
      }
    },

    getParkingSections() {
      return axios({
        method: 'get',
        url: 'https://empresa-service.intrare.app/Parking/sections/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        params: { idEmpresa: this.id_empresa, is_active: true }
      }).then(r => { this.parkingSections = r.data }).catch(e => console.log(e))
    },

    getParkingSpaces() {
      return axios({
        method: 'get',
        url: 'https://empresa-service.intrare.app/Parking/spaces/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        params: { id_section__idEmpresa: this.id_empresa, is_active: true }
      }).then(r => { this.parkingSpaces = r.data }).catch(e => console.log(e))
    },

    getInvitationsStatus() {
      return axios({
        method: 'get',
        url: 'https://empresa-service.intrare.app/Parking/invitations/spaces-status/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        params: {
          empresa_id: this.id_empresa,
          start_date: this.currentDate,
          end_date: this.currentDate,
        }
      }).then(r => {
        this.invitationsStatus = Array.isArray(r.data) ? r.data : []
      }).catch(e => {
        console.log('getInvitationsStatus error:', e)
        this.invitationsStatus = []
      })
    },


    getInvitationDetail(space) {
      return this.invitationsStatus.find(
        inv => Number(inv.space_id) === Number(space.id)
      ) || null
    },

    getSpaceStatus(space) {
      if (space.is_active === false) return 'inactive'
      return space.status || 'available'
    },

    getSpaceAccessRecord(space) {
      const invDetail = this.getInvitationDetail(space)
      return invDetail && invDetail.access_time ? invDetail : null
    },

    getSpaceReservation(space) {
      const invDetail = this.getInvitationDetail(space)
      return invDetail && invDetail.reservation_id ? invDetail : null
    },

    getSpaceGuest(space) {
      const invDetail = this.getInvitationDetail(space)
      if (invDetail && invDetail.visitor_name) return invDetail.visitor_name
      return ''
    },

    getGuestCompany(space) {
      const invDetail = this.getInvitationDetail(space)
      if (invDetail && invDetail.guest_company) return invDetail.guest_company
      return null
    },

    spacesBySection(sectionId) {
      return this.parkingSpaces.filter(sp => sp.id_section === sectionId)
    },

    getSectionName(sectionId) {
      const sec = this.parkingSections.find(s => s.id === sectionId)
      return sec ? sec.name : 'N/A'
    },

    getSpaceCardClass(space) {
      const status = this.getSpaceStatus(space)
      return {
        'space-available': status === 'available',
        'space-occupied': status === 'occupied',
        'space-reserved': status === 'reserved',
        'space-inactive': status === 'inactive',
      }
    },

    getSpaceChipColor(space) {
      const status = this.getSpaceStatus(space)
      if (status === 'occupied') return 'red'
      if (status === 'reserved') return 'orange'
      if (status === 'inactive') return 'grey'
      return 'green'
    },

    getSpaceIconColor(space) {
      const status = this.getSpaceStatus(space)
      if (status === 'occupied') return 'red darken-2'
      if (status === 'reserved') return 'orange darken-2'
      if (status === 'inactive') return 'grey'
      return 'green darken-2'
    },

    getSpaceIcon(space) {
      const status = this.getSpaceStatus(space)
      if (status === 'occupied') return 'directions_car'
      if (status === 'reserved') return 'event_seat'
      if (status === 'inactive') return 'block'
      return 'local_parking'
    },

    getSpaceStatusLabel(space) {
      const status = this.getSpaceStatus(space)
      if (status === 'occupied') return 'Ocupado'
      if (status === 'reserved') return 'Reservado'
      if (status === 'inactive') return 'Inactivo'
      return 'Disponible'
    },

    goNext() {
      const d = new Date(this.currentDate)
      d.setDate(d.getDate() + 1)
      this.currentDate = d.toISOString().substr(0, 10)
      this.fetchAll()
    },

    goPrev() {
      const d = new Date(this.currentDate)
      d.setDate(d.getDate() - 1)
      this.currentDate = d.toISOString().substr(0, 10)
      this.fetchAll()
    },

    goToday() {
      this.currentDate = new Date().toISOString().substr(0, 10)
      this.fetchAll()
    },

    onDateChange(date) {
      this.currentDate = date
      this.fetchAll()
    },

    openSpaceDetail(space) {
      this.selectedSpace = space
      this.dialogSpaceDetail = true
    },

    openChangeStatusDialog() {
      this.changeStatusForm = {
        status: 'available',
        reason: ''
      }
      this.changeStatusError = null
      this.dialogChangeStatus = true
    },

    async handleChangeStatus() {
      // Validate form
      if (!this.$refs.formChangeStatus || !this.$refs.formChangeStatus.validate()) {
        return
      }

      this.loadingChangeStatus = true
      this.changeStatusError = null

      try {
        const token = store.getters.getToken

        if (!token) {
          this.changeStatusError = 'Error de autenticación. Por favor recarga la página.'
          return
        }

        const payload = {
          status: this.changeStatusForm.status
        }

        // Include reason only if provided
        if (this.changeStatusForm.reason && this.changeStatusForm.reason.trim()) {
          payload.reason = this.changeStatusForm.reason.trim()
        }

        const config = {
          method: 'post',
          url: `https://empresa-service.intrare.app/Parking/spaces/${this.selectedSpace.id}/change_status/`,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          },
          data: payload
        }

        const response = await axios(config)

        // Success
        this.dialogChangeStatus = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', `Estado actualizado: ${response.data.old_status} → ${response.data.new_status}`)

        // Reload spaces
        await this.fetchAll()

        // Close detail dialog
        this.dialogSpaceDetail = false
      } catch (error) {
        console.error('Error changing space status:', error)

        if (error.response && error.response.status === 403) {
          this.changeStatusError = error.response.data.error || 'No tienes permisos para cambiar el estado de este espacio.'
        } else if (error.response && error.response.data && error.response.data.error) {
          this.changeStatusError = error.response.data.error
        } else if (error.response && error.response.data && error.response.data.detail) {
          this.changeStatusError = error.response.data.detail
        } else {
          this.changeStatusError = error.message || 'Error al cambiar el estado del espacio'
        }
      } finally {
        this.loadingChangeStatus = false
      }
    },

    startAutoRefresh() {
      if (this.refreshIntervalId) clearInterval(this.refreshIntervalId)
      const savedMin = localStorage.getItem('mockup5_refresh_minutes')
      if (savedMin) this.refreshMinutes = parseInt(savedMin, 10)
      const minutes = this.refreshMinutes && this.refreshMinutes > 0 ? this.refreshMinutes : 2
      this.refreshIntervalId = setInterval(() => {
        if (!this.dialogSpaceDetail) {
          this.fetchAll()
        }
      }, minutes * 60000)
    },

    updateRefreshInterval() {
      if (this.refreshMinutes && this.refreshMinutes > 0) {
        localStorage.setItem('mockup5_refresh_minutes', this.refreshMinutes)
        this.startAutoRefresh()
      }
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(e => console.log(e))
      } else {
        if (document.exitFullscreen) document.exitFullscreen()
      }
    },

    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
      const appEl = document.getElementById('app')
      if (appEl) {
        this.isFullscreen
          ? appEl.classList.add('hide-layout-elements-fullscreen')
          : appEl.classList.remove('hide-layout-elements-fullscreen')
      }
    },
  }
}
</script>

<style scoped>
.space-available {
  border: 2px solid #4CAF50 !important;
  background-color: rgba(76, 175, 80, 0.06) !important;
}
.space-occupied {
  border: 2px solid #F44336 !important;
  background-color: rgba(244, 67, 54, 0.07) !important;
}
.space-reserved {
  border: 2px solid #FF9800 !important;
  background-color: rgba(255, 152, 0, 0.07) !important;
}
.space-inactive {
  border: 2px solid #9E9E9E !important;
  background-color: rgba(158, 158, 158, 0.07) !important;
}
.parking-space-card:hover {
  transform: scale(1.04);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15) !important;
}
.pointer {
  cursor: pointer;
}
</style>
